/**
 * @file apps/web/src/components/PhaseTracker.tsx
 * @description Five-phase project lifecycle tracker (Discovery -> Design ->
 * Build -> Test -> Close). Each phase shows its status, a progress bar, and
 * a one-line stat. Status/progress/stat all come from the live
 * GET /projects/:id/overview endpoint — this component is presentational.
 */

import { CheckCircle2, Lock, Circle } from 'lucide-react';

export interface PhaseStatus {
  phase:    'discovery' | 'design' | 'build' | 'test' | 'close';
  status:   'locked' | 'in_progress' | 'complete';
  progress: number;
  stat:     string;
}

const PHASE_LABELS: Record<PhaseStatus['phase'], string> = {
  discovery: 'Discovery',
  design:    'Design',
  build:     'Build',
  test:      'Test',
  close:     'Close',
};

function PhaseIcon({ status }: { status: PhaseStatus['status'] }) {
  if (status === 'complete') return <CheckCircle2 size={16} className="text-success" />;
  if (status === 'locked') return <Lock size={13} className="text-muted/50" />;
  return <Circle size={13} className="text-blue" fill="currentColor" fillOpacity={0.15} />;
}

function PhaseCard({ phase }: { phase: PhaseStatus }) {
  const isLocked = phase.status === 'locked';
  return (
    <div className={['card p-4', isLocked ? 'opacity-60' : ''].join(' ')}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          <PhaseIcon status={phase.status} />
          <span className="text-sm font-semibold text-ink">{PHASE_LABELS[phase.phase]}</span>
        </div>
        <span className="text-[10px] font-mono text-muted uppercase tracking-wider">
          {phase.status === 'complete' ? 'Complete' : phase.status === 'locked' ? 'Locked' : 'In Progress'}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-gray overflow-hidden mb-2">
        <div
          className="h-full bg-blue transition-[width]"
          style={{ width: `${phase.progress}%` }}
        />
      </div>
      <p className="text-[11px] font-mono text-muted">{phase.stat}</p>
    </div>
  );
}

export function PhaseTracker({ phases }: { phases: PhaseStatus[] }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {phases.map((p) => <PhaseCard key={p.phase} phase={p} />)}
    </div>
  );
}
